import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  Paper,
  Fade,
  Grid,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { callApi } from "./api";

const ClueGame = ({ clueData, theme, titleText, user, styles }) => {
  const [currentClueIndex, setCurrentClueIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [isCorrect, setIsCorrect] = useState(false);

  // Convert the clues object to an array
  const cluesArray = Object.entries(clueData.clues).map(([key, value]) => ({
    id: key,
    ...value,
  }));

  // Sort clues by difficulty (assuming difficulty format like "12 poäng", "10 poäng", etc.)
  const sortedClues = [...cluesArray].sort((a, b) => {
    const pointsA = parseInt(a.difficulty.match(/\d+/)[0]);
    const pointsB = parseInt(b.difficulty.match(/\d+/)[0]);
    return pointsB - pointsA; // Sort from highest to lowest points
  });

  const currentClue = sortedClues[currentClueIndex];

  const handleAnswerChange = (e) => {
    setAnswer(e.target.value);
  };

  const checkAnswer = async () => {
    if (!answer.trim()) return;

    setIsChecking(true);
    try {
      const prompt = `
        Du är en lärare som bedömer elevsvar. Analysera följande:
        
        Fråga: ${clueData.topic}
        Korrekt svar: ${clueData.answer}
        Elevsvar: "${answer}"
        
        Bedöm om elevsvaret är korrekt baserat på innehåll och koncept, inte exakt matchning.
        Svara i följande JSON-format:
        {
          "isCorrect": true/false,
          "explanation": "Kort förklaring av bedömningen"
        }
      `;

      const response = await callApi(
        [
          {
            role: "system",
            content:
              "Du är en expert på att bedöma elevsvar. Svara endast med JSON enligt det specificerade formatet.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        6000,
        true,
        user.uid,
      );

      // Parse the API response
      let responseContent = response.data.choices[0].message.content;

      // Handle potential markdown or code blocks in response
      if (responseContent.includes("```json")) {
        responseContent = responseContent
          .split("```json")[1]
          .split("```")[0]
          .trim();
      } else if (responseContent.includes("```")) {
        responseContent = responseContent
          .split("```")[1]
          .split("```")[0]
          .trim();
      }

      const parsedResult = JSON.parse(responseContent);
      setResult(parsedResult);
      setIsCorrect(parsedResult.isCorrect);

      if (parsedResult.isCorrect) {
        // Calculate points based on current difficulty
        const pointsValue = parseInt(currentClue.difficulty.match(/\d+/)[0]);
        setScore(score + pointsValue);
        setShowFeedback(true);
        setFeedbackDialogOpen(true);

        // If answer is correct, game might be complete or will move to next clue
        if (currentClueIndex === sortedClues.length - 1) {
          setGameCompleted(true);
        }
      } else {
        // If wrong answer, reduce remaining attempts
        setRemainingAttempts(remainingAttempts - 1);
        setFeedbackDialogOpen(true);

        // If no attempts left or on last clue, reveal the answer
        if (
          remainingAttempts <= 1 ||
          currentClueIndex === sortedClues.length - 1
        ) {
          setShowFeedback(true);
          if (currentClueIndex === sortedClues.length - 1) {
            setGameCompleted(true);
          }
        }
      }
    } catch (error) {
      console.error("Error checking answer:", error);
      setResult({
        isCorrect: false,
        explanation:
          "Ett fel uppstod när svaret skulle kontrolleras. Försök igen.",
      });
      setFeedbackDialogOpen(true);
    } finally {
      setIsChecking(false);
    }
  };

  const handleNextClue = () => {
    if (currentClueIndex < sortedClues.length - 1) {
      setCurrentClueIndex(currentClueIndex + 1);
      setAnswer("");
      setResult(null);
      setShowFeedback(false);
      setRemainingAttempts(3);
    }
  };

  const handleCloseFeedback = () => {
    setFeedbackDialogOpen(false);
    if (isCorrect && currentClueIndex < sortedClues.length - 1) {
      handleNextClue();
    }
  };

  const getPointsFromDifficulty = (difficulty) => {
    const match = difficulty.match(/\d+/);
    return match ? match[0] : "0";
  };

  const restart = () => {
    setCurrentClueIndex(0);
    setAnswer("");
    setResult(null);
    setGameCompleted(false);
    setScore(0);
    setShowFeedback(false);
    setRemainingAttempts(3);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "900px", margin: "0 auto" }}>
      <Typography
        variant="h2"
        component="h1"
        align="center"
        gutterBottom
        sx={{
          fontWeight: "600",
          fontSize: { xs: "1.8rem", sm: "2.4rem" },
          mb: 4,
          display: "none",
        }}
      >
        {titleText || "På spåret"}
      </Typography>

      {!gameCompleted ? (
        <Fade in={true}>
          <Card
            elevation={3}
            sx={{
              mb: 4,
              borderRadius: 2,
              ...styles.freshBox,
              p: 0,
              ...styles.softBoxShadow,
            }}
          >
            <Box
              sx={{
                p: 2,
                backgroundColor: theme.palette.primary.main,
                color: "white",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: "bold", fontSize: "2rem" }}
              >
                {currentClue.difficulty}
              </Typography>
            </Box>
            {currentClue.image && (
              <Box sx={{ position: "relative", height: 300 }}>
                <CardMedia
                  component="img"
                  image={currentClue.image}
                  alt={`Ledtråd ${currentClueIndex + 1}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#0f1620" : "#e9ecef",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    padding: 2,
                    background: "rgba(0,0,0,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: "white",
                      fontSize: "1.4rem",
                      textAlign: "center",
                      lineHeight: 1.7,
                      fontWeight: "500",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {currentClue.text}
                  </Typography>
                </Box>
              </Box>
            )}
            <CardContent sx={{ py: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  fontSize: "1.1rem",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  display: "none",
                }}
              >
                {currentClue.text}
              </Typography>

              <Divider sx={{ my: 6, display: "none" }} />
              <Box sx={{ textAlign: "center", mb: 2, mt: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontWeight: 500,
                    color: "text.secondary",
                    display: "none",
                  }}
                >
                  Ditt svar
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  value={answer}
                  onChange={handleAnswerChange}
                  disabled={isChecking || showFeedback}
                  placeholder="Skriv ditt svar här..."
                  autoFocus
                  sx={{
                    mb: 2,

                    width: { xs: "100%", md: "70%" },
                    margin: "0 auto",
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !isChecking && !showFeedback) {
                      checkAnswer();
                    }
                  }}
                  InputProps={{
                    style: {
                      fontWeight: "600",
                      fontSize: "1.2rem",
                      textAlign: "center",
                    },
                  }}
                  inputProps={{
                    style: {
                      textAlign: "center",
                    },
                  }}
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 8 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  {remainingAttempts} försök kvar på denna nivå
                </Typography>

                <Box>
                  <Button
                    variant="purp"
                    onClick={checkAnswer}
                    disabled={isChecking || !answer.trim() || showFeedback}
                    startIcon={
                      isChecking ? (
                        <CircularProgress sx={{ color: "white" }} size={20} />
                      ) : null
                    }
                    sx={{ mr: 2 }}
                  >
                    {isChecking ? "Kontrollerar..." : "Kontrollera svar"}
                  </Button>

                  <Button
                    variant="print"
                    onClick={handleNextClue}
                    disabled={currentClueIndex >= sortedClues.length - 1}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Nästa ledtråd
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      ) : (
        <Fade in={true}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,

              backgroundColor:
                theme.palette.mode === "dark" ? "#192734" : "#f8f9fa",
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 60, color: "#FFD700", mb: 2 }} />

            <Typography variant="h4" gutterBottom>
              Spelet är slut!
            </Typography>

            <Typography variant="h6" sx={{ mb: 3 }}>
              Rätt svar: <strong>{clueData.answer}</strong>
            </Typography>

            <Typography variant="body1" sx={{ mb: 4 }}>
              Du samlade ihop <strong>{score} poäng</strong>!
            </Typography>

            <Button variant="print" onClick={restart} sx={{ mt: 2 }}>
              Börja om
            </Button>
          </Paper>
        </Fade>
      )}

      <Dialog
        open={feedbackDialogOpen}
        onClose={handleCloseFeedback}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: isCorrect ? "#4caf50" : "#f44336",
            color: "white",
            py: 2,
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          {isCorrect ? "Rätt svar!" : "Fel svar"}
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
              mt: 5,
            }}
          >
            {isCorrect ? (
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80 }} />
            ) : (
              <CancelOutlinedIcon color="error" sx={{ fontSize: 80 }} />
            )}
          </Box>

          {!isCorrect && remainingAttempts <= 0 && (
            <Typography variant="body1" sx={{ mt: 2, fontWeight: "medium" }}>
              Rätt svar är: <strong>{clueData.answer}</strong>
            </Typography>
          )}

          {isCorrect && (
            <>
              <Typography
                variant="body1"
                sx={{ mt: 2, fontSize: "1.4rem", textAlign: "center" }}
              >
                Du fick{" "}
                <strong>
                  {getPointsFromDifficulty(currentClue.difficulty)} poäng
                </strong>
                !
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseFeedback} variant="print">
            {isCorrect ? "Stäng" : "Försök igen"}
          </Button>

          {!isCorrect &&
            remainingAttempts > 0 &&
            currentClueIndex < sortedClues.length - 1 && (
              <Button
                onClick={handleNextClue}
                variant="outlined"
                color="secondary"
              >
                Nästa ledtråd
              </Button>
            )}
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          alignItems: "center",
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Ledtråd {currentClueIndex + 1} av {sortedClues.length}
        </Typography>

        <Typography
          variant="h6"
          sx={{ color: "text.secondary", display: "none" }}
        >
          Poäng: {score}
        </Typography>
      </Box>
    </Box>
  );
};

export default ClueGame;
